"use client";

import React from "react";
import { Phone, Mail, Facebook, WandSparkles, MapPin, Calendar, Sparkles, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CONTACT_INFO = {
  phone: "925.494.1662",
  email: "Magic@AbracadabraEvents.co",
  facebook: "@AbracadabraEventsBayArea",
  facebookUrl: "https://facebook.com/AbracadabraEventsBayArea",
  location: "Contra Costa County and the Eastern San Francisco Bay Area"
};
export default function SummonTheMagic() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="summon-the-magic" className="relative w-full py-20 lg:py-32 bg-transparent overflow-hidden" ref={ref}>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          <div className="bg-card/50 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-border/50 shadow-lg">
            <div className="mb-8">
              <h2 className="text-4xl font-['Great_Vibes'] text-primary mb-3 flex items-center gap-3 !text-center md:text-5xl lg:text-7xl">Summon the Magic</h2>
              <p className="text-muted-foreground lg:text-xl">
                Fill out the form below to start planning your magical event. We'll conjure up a quote and get back to you faster than a disappearing act!
              </p>
            </div>

            <FormProvider formId="summon_magic_form_home" sectionName="summon-the-magic" defaultValues={{
            name: "",
            email: "",
            phone: "",
            event_type: "",
            event_date: "",
            location: "",
            details: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            event_type: {
              required: true
            },
            event_date: {
              required: true
            },
            location: {
              required: true
            }
          }} submitText="Summon the Magic" successMessage="Your request has been magically sent! We will be in touch shortly." errorMessage="The magic fizzled out. Please try again or contact us directly.">
              <MagicForm />
            </FormProvider>
          </div>

          <div className="flex flex-col justify-center relative">
            <div className="space-y-10 relative z-10">
              <div>
                <h3 className="text-3xl md:text-4xl lg:text-6xl font-['Great_Vibes'] text-primary mb-6">Contact Us</h3>
                <p className="mb-8 italic text-primary lg:text-xl">Prefer to reach out directly? <br />Our lines are open for all your magical inquiries.</p>
                
                <div className="space-y-6">
                  <ContactItem icon={<Phone className="w-6 h-6" />} label="Call Us" value={CONTACT_INFO.phone} href={`tel:${CONTACT_INFO.phone.replace(/\./g, '-')}`} />
                  <ContactItem icon={<Mail className="w-6 h-6" />} label="Email Us" value={CONTACT_INFO.email} href={`mailto:${CONTACT_INFO.email}`} />
                  <ContactItem icon={<Facebook className="w-6 h-6" />} label="Follow Us" value={CONTACT_INFO.facebook} href={CONTACT_INFO.facebookUrl} isExternal />
                  <ContactItem icon={<MapPin className="w-6 h-6" />} label="Serving" value={CONTACT_INFO.location} />
                </div>
              </div>

              <div className="p-6 bg-primary/5 rounded-xl border border-primary/10">
                <h4 className="text-2xl font-['Great_Vibes'] text-primary mb-2 font-semibold md:text-3xl lg:text-5xl">Magical Promise</h4>
                <p className="text-sm italic text-primary lg:text-xl">"We promise creative, joy-filled, stress-free celebrations — because at Abracadabra Events, magic isn’t optional. It’s part of the plan from the very beginning.<div><br /></div><div>That’s the Abracadabra guarantee."</div></p>
                <div className="mt-4 flex items-center gap-2">
                  <div className="h-px flex-1 bg-primary/20"></div>
                  <span className="font-bold text-primary uppercase tracking-widest italic text-xl font-['Great_Vibes']">KATIE THE PARTY LADY</span>
                  <div className="h-px flex-1 bg-primary/20"></div>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-10 -right-10 opacity-[0.07] pointer-events-none transform rotate-[-15deg] z-0">
              <WandSparkles className="w-80 h-80 text-secondary" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}
function MagicForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2">
          <Sparkles className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-3xl md:text-4xl lg:text-6xl font-['Great_Vibes'] text-primary">Magical Success!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto lg:text-xl">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
            <FormLabel className="lg:text-lg">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="Katie Smith" {...field} className="bg-transparent border-0 border-b border-input/60 rounded-none focus-visible:ring-0 focus:border-secondary transition-all px-0 shadow-none lg:text-lg" />
            </FormControl>
            <FormDescription>First and last name</FormDescription>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
            <FormLabel className="lg:text-lg">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="925-555-1234" {...field} className="bg-transparent border-0 border-b border-input/60 rounded-none focus-visible:ring-0 focus:border-secondary transition-all px-0 shadow-none lg:text-lg" />
            </FormControl>
            <FormDescription>Best number to reach you</FormDescription>
            <FormMessage />
          </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
          <FormLabel className="lg:text-lg">Email Address</FormLabel>
          <FormControl>
            <Input type="email" placeholder="you@example.com" {...field} className="bg-transparent border-0 border-b border-input/60 rounded-none focus-visible:ring-0 focus:border-secondary transition-all px-0 shadow-none lg:text-lg" />
          </FormControl>
          <FormDescription>We'll send the quote to this address</FormDescription>
          <FormMessage />
        </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="event_type" rules={{
        required: "Please select an event type"
      }} render={({
        field
      }) => <FormItem>
            <FormLabel className="lg:text-lg">Type of Event</FormLabel>
            <FormControl>
              <div className="relative">
                <select className="flex h-9 w-full items-center justify-between rounded-none border-0 border-b border-input/60 bg-transparent px-0 py-2 text-sm lg:text-lg placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus:border-secondary disabled:cursor-not-allowed disabled:opacity-50 appearance-none transition-all" {...field} value={field.value || ""}>
                  <option value="" disabled>Select an option</option>
                  <option value="Children's Party">Children's Party</option>
                  <option value="Adult Celebration">Adult Celebration</option>
                  <option value="Corporate Event">Corporate Event</option>
                  <option value="Nonprofit Fundraiser">Nonprofit Fundraiser</option>
                  <option value="Other">Other</option>
                </select>
                <div className="absolute right-3 top-2.5 pointer-events-none text-muted-foreground">
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

        <FormField control={control} name="event_date" rules={{
        required: "Event date is required"
      }} render={({
        field
      }) => <FormItem>
            <FormLabel className="lg:text-lg">Preferred Event Date</FormLabel>
            <FormControl>
              <div className="relative">
                <Input type="date" {...field} className="bg-transparent border-0 border-b border-input/60 rounded-none focus-visible:ring-0 focus:border-secondary transition-all px-0 shadow-none lg:text-lg" />
              </div>
            </FormControl>
            <FormDescription>Select the date you have in mind</FormDescription>
            <FormMessage />
          </FormItem>} />
      </div>

      <FormField control={control} name="location" rules={{
      required: "Event location is required"
    }} render={({
      field
    }) => <FormItem>
          <FormLabel className="lg:text-lg">Event Location</FormLabel>
          <FormControl>
            <Input placeholder="Venue address or description" {...field} className="bg-transparent border-0 border-b border-input/60 rounded-none focus-visible:ring-0 focus:border-secondary transition-all px-0 shadow-none lg:text-lg" />
          </FormControl>
          <FormDescription>Where the magic will happen</FormDescription>
          <FormMessage />
        </FormItem>} />

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
          <FormLabel className="lg:text-lg">Event Details / Magic Vision</FormLabel>
          <FormControl>
            <Textarea placeholder="Describe your event, special requests, theme ideas, etc." className="min-h-[100px] bg-transparent border-0 border-b border-input/60 rounded-none focus-visible:ring-0 focus:border-secondary resize-y px-0 shadow-none transition-all lg:text-lg" {...field} />
          </FormControl>
          <FormDescription>The more details, the better we can tailor the experience</FormDescription>
          <FormMessage />
        </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm lg:text-base">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base lg:text-xl font-semibold bg-secondary text-primary hover:bg-secondary/90 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group rounded-full">
        <span className="relative z-10 flex items-center gap-2">
          {isSubmitting ? <>
              <Loader className="animate-spin w-5 h-5" />
              Summoning...
            </> : <>
              Summon the Magic <WandSparkles className="w-5 h-5 group-hover:rotate-12 transition-transform" />
            </>}
        </span>
        <div className="absolute inset-0 bg-linear-to-tr from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
      </Button>
    </Form>;
}
function ContactItem({
  icon,
  label,
  value,
  href,
  isExternal
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
  isExternal?: boolean;
}) {
  const Content = () => <div className="flex items-start gap-4 group">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center text-secondary-foreground group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300 shadow-sm">
        {icon}
      </div>
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-0.5 lg:text-lg">{label}</p>
        <p className="text-lg font-semibold group-hover:text-primary transition-colors text-primary lg:text-2xl">
          {value}
        </p>
      </div>
    </div>;
  if (href) {
    return <Link to={href} newTab={isExternal} className="block hover:no-underline">
        <Content />
      </Link>;
  }
  return <Content />;
}